import { compact } from 'lodash';

import { AISuggestionMatch } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import {
  TrustDetails_SuccessorTrustee,
  TrustDetails_TrustAdvisor,
  TrustDetails_Trustee,
  TrustDetailsSubformType,
} from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';
import {
  trustAdvisorLevelIndex,
  trusteeLevelIndex,
} from '@/modules/trustees/trustees.constants';
import { AiSuggestionKind } from '@/types/schema';

export function suggestionsToTrusteesSubform(
  matches: AISuggestionMatch[]
): TrustDetailsSubformType['trustees'] {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId)
      .filter((m) => AiSuggestionKind.Trustee == m.suggestion.kind)
      .filter((m) => m.suggestion.trustee)
      .filter((m) => !!m.suggestion.trustee)
      .map(suggestionToTrustee)
  );
}

export function suggestionsToSubtrustTrusteesSubform(
  matches: AISuggestionMatch[]
): TrustDetailsSubformType['trustees'] {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId)
      .filter((m) => AiSuggestionKind.SubtrustTrustee === m.suggestion.kind)
      .filter((m) => m.suggestion.subtrustTrustee)
      .map(suggestionToSubtrustTrustee)
  );
}

export function suggestionsToSuccessorTrusteesSubform(
  matches: AISuggestionMatch[]
): TrustDetailsSubformType['successorTrustees'] {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId)
      .filter((m) => AiSuggestionKind.SuccessorTrustee == m.suggestion.kind)
      .filter((m) => m.suggestion.successorTrustee)
      .filter((m) => !!m.suggestion.successorTrustee)
      .sort((a, b) => {
        const orderA = trusteeLevelIndex(a.suggestion.successorTrustee?.order);
        const orderB = trusteeLevelIndex(b.suggestion.successorTrustee?.order);
        if (orderA === orderB) return 0;
        return orderA - orderB;
      })
      .map(suggestionToSuccessorTrustee)
  );
}

export function suggestionsToSubtrustSuccessorTrusteesSubform(
  matches: AISuggestionMatch[]
): TrustDetailsSubformType['successorTrustees'] {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId)
      .filter(
        (m) => AiSuggestionKind.SubtrustSuccessorTrustee === m.suggestion.kind
      )
      .filter((m) => m.suggestion.subtrustSuccessorTrustee)
      .sort((a, b) => {
        const orderA = trusteeLevelIndex(
          a.suggestion.subtrustSuccessorTrustee?.order
        );
        const orderB = trusteeLevelIndex(
          b.suggestion.subtrustSuccessorTrustee?.order
        );
        if (orderA === orderB) return 0;
        return orderA - orderB;
      })
      .map(suggestionToSubtrustSuccessorTrustee)
  );
}

export function suggestionsToTrustAdvisorsSubform(
  matches: AISuggestionMatch[]
): TrustDetailsSubformType['trustAdvisors'] {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId)
      .filter((m) => m.suggestion.kind === AiSuggestionKind.TrustAdvisor)
      .filter((m) => m.suggestion.trustAdvisor)
      .sort((a, b) => {
        const orderA = trustAdvisorLevelIndex(a.suggestion.trustAdvisor?.order);
        const orderB = trustAdvisorLevelIndex(b.suggestion.trustAdvisor?.order);
        if (orderA === orderB) return 0;
        return orderA - orderB;
      })
      .map(suggestionToTrustAdvisor)
  );
}

export function suggestionsToSubtrustTrustAdvisorsSubform(
  matches: AISuggestionMatch[]
): TrustDetailsSubformType['trustAdvisors'] {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId)
      .filter(
        (m) => m.suggestion.kind === AiSuggestionKind.SubtrustTrustAdvisor
      )
      .filter((m) => m.suggestion.subtrustTrustAdvisor)
      .sort((a, b) => {
        const orderA = trustAdvisorLevelIndex(
          a.suggestion.subtrustTrustAdvisor?.order
        );
        const orderB = trustAdvisorLevelIndex(
          b.suggestion.subtrustTrustAdvisor?.order
        );
        if (orderA === orderB) return 0;
        return orderA - orderB;
      })
      .map(suggestionToSubtrustTrustAdvisor)
  );
}

function suggestionToTrustee(
  suggestionMatch: AISuggestionMatch
): TrustDetails_Trustee | null {
  const clientProfileOrgId =
    suggestionMatch.clientProfileId || suggestionMatch.clientOrganizationId;
  if (!clientProfileOrgId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const { trustee } = suggestionMatch.suggestion;

  return {
    trusteeId: clientProfileOrgId,
    duties: trustee?.duties || [],
    note: '',
    isSuggestion: true,
  };
}

function suggestionToSubtrustTrustee(
  suggestionMatch: AISuggestionMatch
): TrustDetails_Trustee | null {
  const clientProfileOrgId =
    suggestionMatch.clientProfileId || suggestionMatch.clientOrganizationId;
  if (!clientProfileOrgId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const { subtrustTrustee } = suggestionMatch.suggestion;

  return {
    trusteeId: clientProfileOrgId,
    duties: subtrustTrustee?.duties || [],
    note: '',
    isSuggestion: true,
  };
}

function suggestionToSuccessorTrustee(
  suggestionMatch: AISuggestionMatch
): TrustDetails_SuccessorTrustee | null {
  const clientProfileOrgId =
    suggestionMatch.clientProfileId || suggestionMatch.clientOrganizationId;
  if (!clientProfileOrgId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const { successorTrustee } = suggestionMatch.suggestion;

  return {
    successorTrusteeId: clientProfileOrgId,
    level: successorTrustee?.order || '',
    duties: successorTrustee?.duties || [],
    note: '',
    isSuggestion: true,
  };
}

function suggestionToSubtrustSuccessorTrustee(
  suggestionMatch: AISuggestionMatch
): TrustDetails_SuccessorTrustee | null {
  const clientProfileOrgId =
    suggestionMatch.clientProfileId || suggestionMatch.clientOrganizationId;
  if (!clientProfileOrgId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const { subtrustSuccessorTrustee } = suggestionMatch.suggestion;

  return {
    successorTrusteeId: clientProfileOrgId,
    level: subtrustSuccessorTrustee?.order || '',
    duties: subtrustSuccessorTrustee?.duties || [],
    note: '',
    isSuggestion: true,
  };
}

function suggestionToTrustAdvisor(
  suggestionMatch: AISuggestionMatch
): TrustDetails_TrustAdvisor | null {
  const clientProfileOrgId =
    suggestionMatch.clientProfileId || suggestionMatch.clientOrganizationId;
  if (!clientProfileOrgId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const { trustAdvisor } = suggestionMatch.suggestion;

  return {
    trustAdvisorId: clientProfileOrgId,
    level: trustAdvisor?.order || '',
    role: trustAdvisor?.roles || [],
    note: '',
    isSuggestion: true,
  };
}

function suggestionToSubtrustTrustAdvisor(
  suggestionMatch: AISuggestionMatch
): TrustDetails_TrustAdvisor | null {
  const clientProfileOrgId =
    suggestionMatch.clientProfileId || suggestionMatch.clientOrganizationId;
  if (!clientProfileOrgId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const { subtrustTrustAdvisor } = suggestionMatch.suggestion;

  return {
    trustAdvisorId: clientProfileOrgId,
    level: subtrustTrustAdvisor?.order || '',
    role: subtrustTrustAdvisor?.roles || [],
    note: '',
    isSuggestion: true,
  };
}
